import { Controller } from '@hotwired/stimulus';
import * as outbox from '../utils/outbox';

// A message thread. The same message markup is broadcast to both sides, so
// "mine" and "theirs" come from the page's data-viewer. This keeps the thread
// at the bottom as messages arrive, marks theirs read while the page is in
// view, and turns "Sent ✓" into "Seen ✓✓" when the other side reads.
export default class extends Controller {
  static targets = ['messages', 'input'];
  static values = { viewer: String, readUrl: String };

  connect() {
    this.scrollToEnd();
    this.refreshReceipts();
    // With no signal a message waits on the phone and sends later.
    this.form = this.element.querySelector('form#compose');
    this.submitted = (event) => this.queueIfOffline(event);
    this.failed = (event) =>
      event.target.id === 'compose' && this.queue(event.target);
    this.element.addEventListener('submit', this.submitted, true);
    this.element.addEventListener('turbo:fetch-request-error', this.failed);
    this.result = (event) => this.onResult(event.detail);
    window.addEventListener('outbox:result', this.result);
    this.showWaiting();
    this.observer = new MutationObserver((mutations) =>
      this.changed(mutations),
    );
    this.observer.observe(this.element, { childList: true, subtree: true });
  }

  disconnect() {
    this.observer?.disconnect();
    this.element.removeEventListener('submit', this.submitted, true);
    this.element.removeEventListener('turbo:fetch-request-error', this.failed);
    window.removeEventListener('outbox:result', this.result);
  }

  queueIfOffline(event) {
    if (event.target.id !== 'compose' || navigator.onLine) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.queue(event.target);
  }

  async queue(form) {
    const data = new FormData(form);
    const fields = {};
    const photos = [];
    data.forEach((value, key) => {
      if (value instanceof File) {
        if (value.size > 0) photos.push({ blob: value, name: value.name });
      } else fields[key] = value;
    });
    if (!(fields.body || '').trim() && photos.length === 0) return;
    const entry = {
      id: fields.client_key || outbox.newId(),
      kind: 'message',
      url: form.action,
      fields,
      photos,
      text: fields.body || '📷 Photo',
    };
    await outbox.keep(entry);
    form.reset();
    const key = form.querySelector('#compose-key');
    if (key) key.value = outbox.newId();
    this.addWaiting(entry);
  }

  async showWaiting() {
    if (!this.form) return;
    const entries = await outbox.all().catch(() => []);
    entries
      .filter((e) => e.kind === 'message' && e.url === this.form.action)
      .forEach((e) => this.addWaiting(e));
  }

  addWaiting(entry) {
    if (
      !this.hasMessagesTarget ||
      this.messagesTarget.querySelector(`[data-waiting="${entry.id}"]`)
    )
      return;
    const bubble = document.createElement('div');
    bubble.className = 'msg';
    bubble.dataset.side = this.viewerValue;
    bubble.dataset.waiting = entry.id;
    bubble.dataset.at = new Date().toISOString();
    const text = document.createElement('div');
    text.className = 'whitespace-pre-line break-words';
    text.textContent = entry.text;
    const state = document.createElement('span');
    state.className = 'mt-1 block text-right text-[11px] opacity-75';
    state.textContent =
      entry.status === 'refused'
        ? `Not sent: ${entry.message}`
        : '🕓 Waiting for signal';
    bubble.append(text, state);
    this.messagesTarget.append(bubble);
  }

  onResult({ entry, state, data }) {
    const bubble =
      this.hasMessagesTarget &&
      this.messagesTarget.querySelector(`[data-waiting="${entry.id}"]`);
    if (!bubble) return;
    if (state === 'sent') {
      // The real message has this id, so its live copy replaces this one.
      if (data?.message_id) bubble.id = `message_${data.message_id}`;
      bubble.lastChild.textContent = 'Sent ✓';
    } else if (state === 'refused') {
      bubble.lastChild.textContent = `Not sent: ${
        entry.message || data?.message || ''
      }`;
    }
  }

  changed(mutations) {
    // Our own receipt updates are mutations too; reacting to them looped forever
    // and froze the page.
    const relevant = mutations.filter(
      (m) => !m.target.closest?.('[data-receipt]'),
    );
    if (relevant.length === 0) return;
    const added = relevant
      .flatMap((m) => [...m.addedNodes])
      .filter((n) => n.nodeType === 1);
    if (added.some((n) => n.matches?.('[data-side]'))) {
      this.scrollToEnd();
      if (
        added.some((n) => n.dataset.side && n.dataset.side !== this.viewerValue)
      )
        this.markRead();
    }
    this.refreshReceipts();
  }

  scrollToEnd() {
    const box = this.hasMessagesTarget ? this.messagesTarget : null;
    if (box) box.scrollTop = box.scrollHeight;
  }

  markRead() {
    if (!this.readUrlValue || document.visibilityState !== 'visible') return;
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    fetch(this.readUrlValue, {
      method: 'POST',
      headers: { 'X-CSRF-Token': token },
      credentials: 'same-origin',
    });
  }

  refreshReceipts() {
    const other = this.viewerValue === 'store' ? 'staff' : 'store';
    const marker = this.element.querySelector(`#read-marker-${other}`);
    const seenAt = marker?.dataset.at ? Date.parse(marker.dataset.at) : 0;
    this.element
      .querySelectorAll(`[data-side="${this.viewerValue}"] [data-receipt]`)
      .forEach((el) => {
        const at = Date.parse(el.closest('[data-side]').dataset.at);
        const text = seenAt >= at ? ' · Seen ✓✓' : ' · Sent ✓';
        if (el.textContent !== text) el.textContent = text;
      });
  }

  // Enter sends on a keyboard with a Shift key; phones keep Enter for new lines.
  keydown(event) {
    if (
      event.key === 'Enter' &&
      !event.shiftKey &&
      window.matchMedia('(pointer: fine)').matches
    ) {
      event.preventDefault();
      event.target.form.requestSubmit();
    }
  }
}
